import { ApolloError, useApolloClient } from "@apollo/client";
import {
  DialogContentText,
  Paper,
  TableCellProps,
  TableContainer,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import { useSealdGroupReset } from "apollo/hooks/mutations";
import { useSealdGroupSessionsCheck } from "apollo/hooks/queries";
import { composeProgress } from "apollo/utils";
import { UPDATE_ACCOUNT } from "core/apollo/graphql";
import {
  ACCOUNT_ACTION_ACTIVATE,
  ACCOUNT_STATUS_ACTIVE,
  ACCOUNT_STATUS_CREATED,
  ACCOUNT_STATUS_INACTIVE,
  QUERY_PROGRESS_SUCCEED,
  SEALD_ENCRYPTION_BROKEN_ACCESS,
  SEALD_ENCRYPTION_FULL_ACCESS,
  SEALD_ENCRYPTION_NO_ACCESS,
  SEALD_ENCRYPTION_NO_GROUP_CREATED,
  SEALD_ENCRYPTION_PARTIAL_ACCESS,
  SEALD_ENCRYPTION_PARTIAL_ACCESS_WITH_PENDING,
  SEALD_ENCRYPTION_PENDING_ACCESS,
  SEALD_ENCRYPTION_USER_NOT_REGISTERED,
} from "core/consts";
import { useDateLocale } from "core/hooks/useDateLocale";
import { getName } from "core/model/accounts";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import {
  Account,
  AccountRole,
  Account as AccountType,
  Careseeker,
  GetOntologyType,
  Roles,
  SealdEncryptionAccessStatus,
} from "core/types";
import { format, fromUnixTime } from "date-fns";
import ConfirmationDialog from "ds_legacy/components/ConfirmationDialog";
import InfoCard, { CardContentContainer } from "ds_legacy/components/InfoCard";
import SpeedDial, { SpeedDialAction } from "ds_legacy/components/SpeedDial";
import Tooltip from "ds_legacy/components/Tooltip";
import { ICON_DARK } from "ds_legacy/materials/colors";
import {
  ACP_CARD_WIDTH,
  dp,
  margin,
  padding,
  space,
} from "ds_legacy/materials/metrics";
import {
  Body,
  Email,
  FONT_SIZE_11,
  FONT_SIZE_12,
  FONT_SIZE_16,
} from "ds_legacy/materials/typography";
import { TableHead, TableRow } from "dsl/atoms/Table";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { Chip, roleNameToHex } from "dsl/organisms/EventTimeline/Events";
import { CopyIcon, LockIcon, MailIcon, UserPlusIcon } from "lucide-react";
import styled from "styled-components";
import { useLocale, useTranslations } from "translations";
import Translations from "translations/types";

const SpeedDialPositioner = styled.div`
  position: absolute;
  z-index: 1;
  top: ${dp(-28)};
  right: 0;
`;

export const getCareseekerRoles = (
  roles: Roles | undefined,
  careseekerId: number,
): AccountRole[] => {
  const careseekerRoles =
    roles?.careseeker_roles && roles.careseeker_roles.length > 0
      ? roles.careseeker_roles.find(
          ({ careseeker }) => careseeker?.id === careseekerId || false,
        )
      : undefined;

  return careseekerRoles?.roles?.filter((r) => r && r > 0) || [];
};

export const getCareproviderRoles = (
  roles: Roles | undefined,
  careproviderId: number,
): AccountRole[] => {
  const careproviderRoles =
    roles?.careprovider_roles && roles.careprovider_roles.length > 0
      ? roles.careprovider_roles.find(
          (r) => r?.careprovider?.id === careproviderId || false,
        )
      : undefined;

  return careproviderRoles?.roles?.filter((r: ToType) => r && r > 0) || [];
};

export const extractDistinctCareproviderRoles = (
  roles: Roles | undefined,
  careproviderId: number,
): AccountRole[] => {
  if (!roles?.careprovider_roles) return [];

  const uniqueRoles = new Set<AccountRole>();

  for (const careprovider_roles of roles.careprovider_roles) {
    if (careprovider_roles?.careprovider?.id !== careproviderId) continue;
    for (const role of careprovider_roles.roles ?? []) {
      uniqueRoles.add(role);
    }
  }

  return Array.from(uniqueRoles);
};

function mapRoleIdsToLabels(
  roleIds: number[],
  getOntology: GetOntologyType,
  entityType: string,
): string[] {
  return roleIds
    .map((id) => {
      const role = getOntology({ key: id, type: "accountRoleShort" });
      if (!role || role.trim() === "") {
        console.error(
          `Empty role value returned for ${entityType} role ID: ${id}`,
        );
        return "";
      }
      return role;
    })
    .filter(Boolean);
}

function getRoles(
  roles: Roles | undefined,
  careseekerId: number | undefined,
  careproviderId: number | undefined,
  getOntology: GetOntologyType,
) {
  if (!roles) return [];

  if (careseekerId) {
    const careseekerRoleIds = getCareseekerRoles(roles, careseekerId);
    return mapRoleIdsToLabels(careseekerRoleIds, getOntology, "careseeker");
  }

  if (careproviderId) {
    const careproviderRoleIds = getCareproviderRoles(roles, careproviderId);
    return mapRoleIdsToLabels(careproviderRoleIds, getOntology, "careprovider");
  }

  return [];
}

export function updateRoles({
  careproviderId,
  careseekerId,
  newRoles,
  roles,
}: {
  careproviderId?: number;
  careseekerId?: number;
  newRoles?: AccountRole[];
  roles: Roles;
}): Roles {
  if (careseekerId !== undefined) {
    // Update careseeker roles
    const updatedCareseekerRoles = roles.careseeker_roles?.map((role) => {
      if (role.careseeker?.id === careseekerId) {
        return { ...role, roles: newRoles };
      }
      return role;
    });
    return {
      ...roles,
      careseeker_roles: updatedCareseekerRoles,
    };
  }

  if (careproviderId !== undefined) {
    // Update careprovider roles
    const updatedCareproviderRoles = roles.careprovider_roles?.map((role) => {
      if (role.careprovider?.id === careproviderId) {
        return { ...role, roles: newRoles };
      }
      return role;
    });
    return {
      ...roles,
      careprovider_roles: updatedCareproviderRoles,
    };
  }

  // Update admin roles
  roles.admin_roles = newRoles || [];

  return roles;
}

function Actions({
  accounts,
  getOntology,
  isSSOActivated,
  pageState,
  sealdId,
  setPageState,
  translations,
}: {
  accounts: Readonly<Array<AccountType>>;
  getOntology: GetOntologyType;
  isSSOActivated?: boolean;
  pageState: any;
  sealdId: string | undefined;
  setPageState: (state: any) => void;
  translations: Translations;
}) {
  const actions: SpeedDialAction[] = [];

  if (isSSOActivated) {
    if (sealdId) {
      actions.push({
        icon: <LockIcon size={FONT_SIZE_16} style={{ color: ICON_DARK }} />,
        onClick: () => setPageState({ type: "resetGroup" }),
        testId: "reset-group",
        tooltipTitle: translations.acp.resetGroups.menuResetGroup,
      });
    }
  } else {
    actions.push(
      {
        icon: <UserPlusIcon size={FONT_SIZE_16} style={{ color: ICON_DARK }} />,
        onClick: () => setPageState({ type: "addAccount" }),
        testId: "add_account",
        tooltipTitle: translations.acp.addAccount,
      },
      {
        icon: <CopyIcon size={FONT_SIZE_16} style={{ color: ICON_DARK }} />,
        onClick: () => setPageState({ type: "importAccounts" }),
        testId: "import_accounts",
        tooltipTitle: translations.acp.excelImportAccounts,
      },
    );

    if (sealdId) {
      actions.push({
        icon: <LockIcon size={FONT_SIZE_16} style={{ color: ICON_DARK }} />,
        onClick: () => setPageState({ type: "resetGroup" }),
        testId: "reset-group",
        tooltipTitle: translations.acp.resetGroups.menuResetGroup,
      });
    }

    const createdAccounts = accounts?.filter(
      (a) => a.status == ACCOUNT_STATUS_CREATED,
    );

    if (createdAccounts && createdAccounts.length > 0) {
      actions.push({
        icon: <MailIcon />,
        onClick: () => setPageState({ type: "batchActivate" }),
        testId: "activate-accounts",
        tooltipTitle: translations.acp.batchSendActivation,
      });
    }
  }

  return (
    <>
      <SpeedDialPositioner>
        <SpeedDial
          actions={actions}
          testId="account_actions"
          tooltip="Account Actions"
        />
      </SpeedDialPositioner>
      {pageState && pageState.type === "batchActivate" && (
        <BatchActivate
          accounts={accounts.filter((a) => a.status == ACCOUNT_STATUS_CREATED)}
          getOntology={getOntology}
          setPageState={setPageState}
          translations={translations}
        />
      )}
      {pageState && pageState.type === "resetGroup" && (
        <GroupResetDialog sealdId={sealdId} setPageState={setPageState} />
      )}
    </>
  );
}

function GroupResetDialog({
  sealdId,
  setPageState,
}: {
  sealdId: string | undefined;
  setPageState: (state: any) => void;
}) {
  const toast = useToast();
  const translations = useTranslations();

  const close = () => setPageState({});

  const [mutate, queryProgress] = useSealdGroupReset({
    onCompleted() {
      close();
      toast({
        color: "success",
        message: translations.acp.resetGroups.successMessage,
      });
    },
    onError: (error: ApolloError) => {
      toast({ color: "danger", message: error.message });
    },
  });

  const [progress, data, { error }] = useSealdGroupSessionsCheck({
    sealdId,
  });

  return (
    <ConfirmationDialog
      confirmButtonProps={{
        disabled: !data || progress !== QUERY_PROGRESS_SUCCEED,
      }}
      id="reset-group"
      nodeContent={
        data ? (
          <>
            {translations.acp.resetGroups.resetGroupsSubtitle({
              sessionNumber:
                data.sealdGroupSessions.pending_seald_access_count.toString(),
            })}
            <TableContainer component={Paper} style={{ maxWidth: 600 }}>
              <Table
                aria-label={translations.acp.resetGroups.columnLostSession}
                sx={{ overflowX: "auto" }}
              >
                <TableHead>
                  <TableCell component="th" scope="row">
                    {translations.acp.resetGroups.columnLostSession}
                  </TableCell>
                  <TableCell align="center">
                    {translations.acp.resetGroups.columnCount}
                  </TableCell>
                </TableHead>
                <TableBody>
                  <TableRow key="patients">
                    <TableCell component="th" scope="row">
                      {translations.acp.resetGroups.activePatients}
                    </TableCell>
                    <TableCell align="center">
                      {data.sealdGroupSessions.lost_sessions.active_patients}
                    </TableCell>
                  </TableRow>
                  <TableRow key="files">
                    <TableCell component="th" scope="row">
                      {translations.acp.resetGroups.activeFiles}
                    </TableCell>
                    <TableCell align="center">
                      {data.sealdGroupSessions.lost_sessions.active_files}
                    </TableCell>
                  </TableRow>
                  <TableRow key="requests">
                    <TableCell component="th" scope="row">
                      {translations.acp.resetGroups.activeRequests}
                    </TableCell>
                    <TableCell align="center">
                      {data.sealdGroupSessions.lost_sessions.active_requests}
                    </TableCell>
                  </TableRow>
                  <TableRow key="archived">
                    <TableCell component="th" scope="row">
                      {translations.acp.resetGroups.archived}
                    </TableCell>
                    <TableCell align="center">
                      {data.sealdGroupSessions.lost_sessions.archived}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <>
            <p>{error?.message}</p>
          </>
        )
      }
      opened
      queryProgress={composeProgress([queryProgress, progress])}
      title={translations.acp.resetGroups.resetGroupsTitle}
      onCancel={() => close()}
      onSubmit={() => mutate({ seald_id: sealdId })}
    />
  );
}

function BatchActivate({
  accounts,
  getOntology,
  setPageState,
  translations,
}: {
  accounts: Array<Account>;
  getOntology: GetOntologyType;
  setPageState: (state: any) => void;
  translations: Translations;
}) {
  const client = useApolloClient();
  const created = accounts.filter((a) => a.status == ACCOUNT_STATUS_CREATED);
  const close = () => setPageState({});
  const toast = useToast();

  const resendActivation = (accountId: number) =>
    client?.mutate({
      mutation: UPDATE_ACCOUNT,
      variables: {
        id: accountId,
        input: {
          action: {
            type: ACCOUNT_ACTION_ACTIVATE,
            version: "2",
          },
        },
      },
    });

  return (
    <ConfirmationDialog
      componentContent={
        <div>
          {created.map((c) => (
            <DialogContentText key={c.id}>
              {getName(c, getOntology)}
            </DialogContentText>
          ))}
        </div>
      }
      id="patient_success_dialog"
      opened
      submitText="OK"
      title={translations.acp.batchSendActivation}
      onCancel={close}
      onSubmit={() =>
        Promise.all(created.map((account) => resendActivation(account.id)))
          .then(() => {
            toast({
              color: "success",
              message: translations.acp.notifyActivationSent,
            });
            close();
          })
          .catch(function (err) {
            toast({
              color: "primary",
              message: JSON.stringify(err),
            });
          })
      }
    />
  );
}

const statusPrioMap: Map<number, number> = new Map([
  [ACCOUNT_STATUS_ACTIVE, 1],
  [ACCOUNT_STATUS_CREATED, 2],
  [ACCOUNT_STATUS_INACTIVE, 3],
]);

const ENCRYPTION_STATUS_CONFIG: {
  [encryptionStatus in SealdEncryptionAccessStatus]: {
    color: string;
    label: string;
    tooltip: string;
  };
} = {
  [SEALD_ENCRYPTION_BROKEN_ACCESS]: {
    color: "red",
    label: "broken access",
    tooltip:
      "something went wrong in the access creation. please check with engineering.",
  },
  [SEALD_ENCRYPTION_FULL_ACCESS]: {
    color: "green",
    label: "full access",
    tooltip: "has access to view and create encrypted data",
  },
  [SEALD_ENCRYPTION_NO_ACCESS]: {
    color: "gray",
    label: "no access",
    tooltip: "not in group yet plus no pending access",
  },
  [SEALD_ENCRYPTION_NO_GROUP_CREATED]: {
    color: "gray",
    label: "no group",
    tooltip:
      "No group has been created yet. Likely no one logged in yet or they are having Seald connection issues.",
  },
  [SEALD_ENCRYPTION_PARTIAL_ACCESS]: {
    color: "red",
    label: "partial access without key",
    tooltip:
      "user only has access to the sessions they created. there isn't a pending group access. this should be fixed over night",
  },
  [SEALD_ENCRYPTION_PARTIAL_ACCESS_WITH_PENDING]: {
    color: "orange",
    label: "partial access",
    tooltip:
      "user only has access to the sessions they created. there is a pending group access. one of the accounts with access in green needs to open the app",
  },
  [SEALD_ENCRYPTION_PENDING_ACCESS]: {
    color: "orange",
    label: "pending access",
    tooltip:
      "no access yet. one of the accounts with access in green needs to open the app",
  },
  [SEALD_ENCRYPTION_USER_NOT_REGISTERED]: {
    color: "gray",
    label: "not registered",
    tooltip: "user is not registered, which should happen by login in",
  },
};

function EncryptionAccessStatusChip({
  encryptionStatus,
}: {
  encryptionStatus: Account["seald_encryption_status"];
}) {
  if (encryptionStatus == null) return null;

  const { color, label, tooltip } = ENCRYPTION_STATUS_CONFIG[encryptionStatus];

  return (
    <Tooltip placement="left" title={tooltip}>
      <div
        style={{
          alignItems: "center",
          display: "flex",
          flexFlow: "wrap",
          minHeight: space(7),
        }}
      >
        <Chip
          key={encryptionStatus}
          chipMargin="0"
          color={color}
          label={label}
        />
      </div>
    </Tooltip>
  );
}

const AccountsTableCell = (props: TableCellProps) => (
  <TableCell style={{ padding: padding(1.5) }} {...props} />
);

export function AccountsTable({
  accounts,
  careproviderId,
  careseekerId,
  goToAccount,
  isSSOActivated,
  pageState,
  sealdId,
  setPageState,
  title,
  withAddAccount = true,
}: {
  accounts: Readonly<Account[]>;
  careproviderId?: number;
  careseeker?: Careseeker;
  careseekerId?: number;
  goToAccount: (account: number, page?: string) => void;
  isSSOActivated?: boolean;
  pageState: { accountId?: number; type?: string };
  sealdId: string | undefined;
  setPageState: (state: { accountId?: number; type?: string }) => void;
  title: string;
  withAddAccount?: boolean;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  const locale = useLocale();
  const { currentLocale } = useDateLocale();

  return (
    <InfoCard position="relative" themed="light" title={title}>
      <CardContentContainer noPadding overflowX="auto">
        <Table sx={{ overflowX: "auto" }}>
          <TableHead>
            <AccountsTableCell>#</AccountsTableCell>
            <AccountsTableCell>Name</AccountsTableCell>
            <AccountsTableCell>Roles</AccountsTableCell>
            <AccountsTableCell>Last Login</AccountsTableCell>
            <AccountsTableCell>Status</AccountsTableCell>
            <AccountsTableCell>Encryption Access</AccountsTableCell>
          </TableHead>
          {accounts && (
            <TableBody>
              {accounts
                .clone()
                .sort((x, y) => {
                  if (x.status != y.status) {
                    return (
                      (statusPrioMap.get(x.status || 0) || 0) -
                      (statusPrioMap.get(y.status || 0) || 0)
                    );
                  }
                  return (x.last_name || "").localeCompare(y.last_name || "");
                })
                .map((a) => {
                  return (
                    <TableRow
                      key={a.id}
                      style={{
                        maxWidth: ACP_CARD_WIDTH,
                        opacity: a.status == ACCOUNT_STATUS_INACTIVE ? 0.6 : 1,
                      }}
                      onClick={() => goToAccount(a.id)}
                    >
                      <AccountsTableCell>
                        <Body fontSize={FONT_SIZE_12} margin={margin(0)}>{`#${
                          a.id || ""
                        }`}</Body>
                      </AccountsTableCell>
                      <AccountsTableCell style={{ maxWidth: dp(145) }}>
                        <Body
                          breakWord
                          fontSize={FONT_SIZE_12}
                          margin={margin(0)}
                          maxWidth={dp(145)}
                        >
                          {getName(a, getOntology, {
                            withAcademicTitle: true,
                            withSalutation: true,
                          })}
                        </Body>
                        {a.email && (
                          <Email
                            fontWeight={300}
                            margin={margin(0)}
                            style={{ fontSize: FONT_SIZE_11 }}
                            textBreak
                          >
                            {a.email}
                          </Email>
                        )}
                      </AccountsTableCell>
                      <AccountsTableCell>
                        <div
                          style={{
                            alignItems: "center",
                            display: "flex",
                            flexFlow: "wrap",
                            minHeight: space(7),
                            width: space(21),
                          }}
                        >
                          {getRoles(
                            a.roles,
                            careseekerId,
                            careproviderId,
                            getOntology,
                          )
                            .sort()
                            .map((name) => (
                              <Chip
                                key={name}
                                chipMargin={dp(1)}
                                color={roleNameToHex(name)}
                                label={name}
                              />
                            ))}
                        </div>
                      </AccountsTableCell>
                      <AccountsTableCell>
                        <Body fontSize={FONT_SIZE_12} margin={margin(0)}>
                          {a.last_login
                            ? `${
                                locale &&
                                format(fromUnixTime(a.last_login), "PPpp", {
                                  locale: currentLocale,
                                })
                              }`
                            : translations.general.never}
                        </Body>
                      </AccountsTableCell>
                      <AccountsTableCell>
                        <Body fontSize={FONT_SIZE_12} margin={margin(0)}>
                          {getOntology({
                            key: a.status,
                            type: ONTOLOGIES.ACCOUNT_STATUS,
                          })}
                        </Body>
                      </AccountsTableCell>
                      <AccountsTableCell style={{ paddingRight: 0 }}>
                        <EncryptionAccessStatusChip
                          encryptionStatus={a.seald_encryption_status}
                        />
                      </AccountsTableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          )}
        </Table>
        {withAddAccount && (
          <Actions
            accounts={accounts}
            getOntology={getOntology}
            isSSOActivated={isSSOActivated}
            pageState={pageState}
            sealdId={sealdId}
            setPageState={setPageState}
            translations={translations}
          />
        )}
      </CardContentContainer>
    </InfoCard>
  );
}
